import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { RoundTypeSelector } from './RoundTypeSelector';
import { ValuationGuidanceCard } from './ValuationGuidanceCard';
import { DilutionPreviewCard } from './DilutionPreviewCard';
import { RoundReviewPanel } from './RoundReviewPanel';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Rocket, Save } from 'lucide-react';

interface FoundingRoundWizardProps {
  companyId: string;
  onComplete?: () => void;
}

const STEPS = [
  'Choose your round',
  'Set your raise target',
  'Pick your valuation',
  'Preview dilution',
  'Add key terms',
  'Review your round',
];

export function FoundingRoundWizard({ companyId, onComplete }: FoundingRoundWizardProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    roundType: 'SEED',
    raiseAmount: '',
    currency: 'USD',
    preMoney: 0,
    instrument: 'EQUITY',
    optionPoolIncrease: '',
    leadInvestor: '',
    valuationCap: '',
    discountRate: '',
    sharePrice: '',
  });

  const update = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const raiseNum = parseFloat(form.raiseAmount.replace(/[^0-9.]/g, '')) || 0;
  const poolNum = parseFloat(form.optionPoolIncrease) / 100 || 0;

  const canNext = () => {
    if (step === 0) return !!form.roundType;
    if (step === 1) return raiseNum > 0;
    if (step === 2) return form.preMoney > 0;
    return true;
  };

  const instrumentForType = (rt: string) => {
    if (rt === 'SAFE') return 'SAFE';
    if (rt === 'BRIDGE') return 'CONVERTIBLE_NOTE';
    return 'EQUITY';
  };

  const handleCreate = async (status: 'DRAFT' | 'OPEN') => {
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const typeLabel = form.roundType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

      const { error } = await supabase.from('funding_rounds').insert({
        company_id: companyId,
        name: `${typeLabel} Round`,
        round_type: form.roundType,
        target_amount: raiseNum || null,
        currency: form.currency,
        pre_money: form.preMoney || null,
        post_money: form.preMoney && raiseNum ? form.preMoney + raiseNum : null,
        share_price: parseFloat(form.sharePrice) || null,
        option_pool_increase: poolNum || null,
        instrument: form.instrument,
        valuation_cap: parseFloat(form.valuationCap) || null,
        discount_rate: parseFloat(form.discountRate) || null,
        lead_investor: form.leadInvestor || null,
        status,
        created_by: user.id,
      });

      if (error) throw error;

      await supabase.from('audit_log').insert({
        user_id: user.id,
        action: 'FUNDING_ROUND_CREATED',
        entity_type: 'funding_round',
        company_id: companyId,
        details: { name: `${typeLabel} Round`, round_type: form.roundType, status },
      });

      toast.success(status === 'DRAFT' ? 'Round saved as draft' : 'Round created successfully');
      onComplete?.();
      navigate('/transactions');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create round');
    } finally {
      setSaving(false);
    }
  };

  const slideVariants = {
    enter: { opacity: 0, x: 30 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -30 },
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
          Step {step + 1} of {STEPS.length}
        </p>
        <h2 className="text-2xl font-semibold">{STEPS[step]}</h2>
      </div>

      <Progress value={((step + 1) / STEPS.length) * 100} className="h-1" />

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.2 }}
          className="min-h-[300px]"
        >
          {/* Step 0: Round Type */}
          {step === 0 && (
            <RoundTypeSelector
              value={form.roundType}
              onChange={(v) => update({ roundType: v, instrument: instrumentForType(v) })}
            />
          )}

          {/* Step 1: Raise Target */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This is the amount you plan to raise in this round.
              </p>
              <div className="space-y-2">
                <Label>Raise amount</Label>
                <Input
                  type="text"
                  placeholder="e.g. 2,000,000"
                  value={form.raiseAmount}
                  onChange={(e) => update({ raiseAmount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => update({ currency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="ZAR">ZAR</SelectItem>
                    <SelectItem value="AED">AED</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Valuation */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Based on your current valuation guidance, this range appears reasonable.
              </p>
              <ValuationGuidanceCard
                companyId={companyId}
                preMoney={form.preMoney}
                onPreMoneyChange={(v) => update({ preMoney: v })}
              />
            </div>
          )}

          {/* Step 3: Dilution */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This preview shows how ownership changes if you proceed with this round.
              </p>
              <DilutionPreviewCard
                preMoney={form.preMoney}
                raiseAmount={raiseNum}
                optionPoolIncrease={poolNum}
                currency={form.currency}
              />
            </div>
          )}

          {/* Step 4: Terms */}
          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Add the key terms for your round. Only fill what's relevant.
              </p>

              {(form.instrument === 'SAFE' || form.instrument === 'CONVERTIBLE_NOTE') && (
                <>
                  <div className="space-y-2">
                    <Label>Valuation cap</Label>
                    <Input
                      type="text"
                      placeholder="e.g. 10000000"
                      value={form.valuationCap}
                      onChange={(e) => update({ valuationCap: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Discount rate (%)</Label>
                    <Input
                      type="text"
                      placeholder="e.g. 20"
                      value={form.discountRate}
                      onChange={(e) => update({ discountRate: e.target.value })}
                    />
                  </div>
                </>
              )}

              {form.instrument === 'EQUITY' && (
                <>
                  <div className="space-y-2">
                    <Label>Lead investor</Label>
                    <Input
                      placeholder="e.g. Sequoia Capital"
                      value={form.leadInvestor}
                      onChange={(e) => update({ leadInvestor: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Option pool increase (%)</Label>
                    <Input
                      type="text"
                      placeholder="e.g. 10"
                      value={form.optionPoolIncrease}
                      onChange={(e) => update({ optionPoolIncrease: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Share price</Label>
                    <Input
                      type="text"
                      placeholder="Optional"
                      value={form.sharePrice}
                      onChange={(e) => update({ sharePrice: e.target.value })}
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>Instrument</Label>
                <Select value={form.instrument} onValueChange={(v) => update({ instrument: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EQUITY">Equity</SelectItem>
                    <SelectItem value="SAFE">SAFE</SelectItem>
                    <SelectItem value="CONVERTIBLE_NOTE">Convertible Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {step === 5 && (
            <div className="space-y-4">
              <RoundReviewPanel
                roundType={form.roundType}
                raiseAmount={raiseNum}
                currency={form.currency}
                preMoney={form.preMoney}
                instrument={form.instrument}
                optionPoolIncrease={poolNum}
                leadInvestor={form.leadInvestor}
                valuationCap={parseFloat(form.valuationCap) || 0}
                discountRate={parseFloat(form.discountRate) || 0}
              />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <Button
          variant="ghost"
          onClick={() => setStep((s) => s - 1)}
          disabled={step === 0}
          className="gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>

        <div className="flex gap-2">
          {step === STEPS.length - 1 ? (
            <>
              <Button variant="outline" onClick={() => handleCreate('DRAFT')} disabled={saving}>
                <Save className="h-4 w-4 mr-1.5" /> Save as Draft
              </Button>
              <Button onClick={() => handleCreate('OPEN')} disabled={saving}>
                <Rocket className="h-4 w-4 mr-1.5" /> Create Round
              </Button>
            </>
          ) : (
            <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} className="gap-1.5">
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
